/* eslint no-underscore-dangle: 0 */
/**
 * Bottom-right alert nudging a seller toward campaigns they have not joined.
 *
 * Shows a campaign only when registration is still open AND the seller has uploaded
 * nothing to it, so a seller who has already participated is never nagged. Dismissing hides
 * that campaign for the rest of the day only - if the seller still has not uploaded
 * anything tomorrow, it comes back. Stored per seller, so a shared machine does not leak
 * one seller's dismissals to another.
 *
 * Mounted once in Layout, so it follows the seller around the app rather than only
 * appearing on the Campaigns page they would have to visit anyway.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Card } from 'react-bootstrap';
import { format } from 'date-fns';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import { DEFAULT_COUNTRY } from 'constants/index';
import { getSellerHubPromotions } from 'services/ApiClientPricelyService';
import { isRegistrationClosed } from 'helpers/promotions';

// One page of promotions is plenty - the alert only ever shows the first match.
const FETCH_LIMIT = 20;

const getDismissedKey = (sellerEmail) => `campaignAlertDismissed:${sellerEmail}`;

// Local calendar day, so "today" means the seller's own day rather than UTC's.
const getToday = () => format(new Date(), 'yyyy-MM-dd');

// Stored as { [promotionId]: 'YYYY-MM-DD' } - the day it was dismissed.
const readDismissed = (sellerEmail) => {
  try {
    const stored = window.localStorage.getItem(getDismissedKey(sellerEmail));
    const parsed = stored ? JSON.parse(stored) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    // A corrupt or unavailable localStorage should not stop the alert working.
    return {};
  }
};

const writeDismissed = (sellerEmail, dismissed) => {
  try {
    window.localStorage.setItem(getDismissedKey(sellerEmail), JSON.stringify(dismissed));
  } catch (e) {
    // Private browsing or a full quota - the alert just reappears next load.
  }
};

const CampaignAlert = () => {
  const history = useHistory();
  const { currentUser } = useSelector((state) => state.auth);
  const sellerEmail = currentUser?.email || '';

  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    if (!sellerEmail) return undefined;

    let cancelled = false;

    const findUnjoinedCampaign = async () => {
      try {
        const response = await getSellerHubPromotions({
          page: 1,
          limit: FETCH_LIMIT,
          search: '',
          startDate: '',
          endDate: '',
          store: '',
          country: DEFAULT_COUNTRY,
          sellerEmail,
        });
        const { success, data } = response?.data || {};
        if (!success || cancelled) return;

        const dismissed = readDismissed(sellerEmail);
        const today = getToday();
        const matches = (data?.items || []).filter(
          (item) => !isRegistrationClosed(item) && !item?.sellerProductCounts?.total && dismissed[item._id] !== today
        );

        setPromotions(matches);
      } catch (e) {
        // The alert is a nudge, not core functionality - stay silent on failure.
      }
    };

    findUnjoinedCampaign();

    return () => {
      cancelled = true;
    };
  }, [sellerEmail]);

  // Closing dismisses every campaign currently listed, for the rest of today.
  const handleDismiss = () => {
    const today = getToday();
    const dismissed = readDismissed(sellerEmail);
    // Drop entries from previous days while writing, so the map cannot grow unbounded.
    const pruned = Object.fromEntries(Object.entries(dismissed).filter(([, day]) => day === today));
    promotions.forEach((item) => {
      pruned[item._id] = today;
    });
    writeDismissed(sellerEmail, pruned);
    setPromotions([]);
  };

  // Hide without recording a dismissal - the seller is on their way to participate, and if
  // they do not follow through the alert should still be there tomorrow.
  const handleParticipate = () => {
    setPromotions([]);
    history.push('/marketing/promotions?tab=sale');
  };

  if (!promotions.length) return null;

  const isSingle = promotions.length === 1;

  return (
    <Card
      className="shadow-lg border-0"
      style={{
        position: 'fixed',
        right: '1rem',
        bottom: '1rem',
        width: '400px',
        maxWidth: 'calc(100vw - 2rem)',
        zIndex: 1035,
        background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-darker) 100%)',
      }}
    >
      <Card.Body className="p-4" style={{ color: 'var(--light-text)' }}>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center gap-2">
            <CsLineIcons icon="promo" size="18" />
            <span className="fw-bold text-uppercase text-small" style={{ letterSpacing: '0.05em', opacity: 0.85 }}>
              {isSingle ? 'New Campaign' : `${promotions.length} New Campaigns`}
            </span>
          </div>
          {/* A plain button, not react-bootstrap's: .btn-link forces
              `color: var(--primary) !important`, which would be navy on navy here. */}
          <button
            type="button"
            onClick={handleDismiss}
            title="Dismiss"
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, cursor: 'pointer', color: 'var(--light-text)', opacity: 0.75 }}
          >
            <CsLineIcons icon="close" size="16" />
          </button>
        </div>

        {/* Grows with the number of campaigns, then scrolls rather than running off screen. */}
        <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
          {promotions.map((item, index) => (
            <div
              key={item._id}
              className={index ? 'pt-3 mt-3' : ''}
              style={index ? { borderTop: '1px solid rgba(255,255,255,0.2)' } : undefined}
            >
              {isSingle ? (
                <h4 className="fw-bold mb-2" style={{ color: 'var(--light-text)' }}>
                  {item.promotionName}
                </h4>
              ) : (
                <h5 className="fw-bold mb-1" style={{ color: 'var(--light-text)' }}>
                  {item.promotionName}
                </h5>
              )}
              {item.regClosingDate && (
                <div className="text-small" style={{ opacity: 0.9 }}>
                  Registration Open until {format(new Date(item.regClosingDate), 'MMMM d, yyyy')}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-small mt-3 mb-3" style={{ opacity: 0.75 }}>
          You have not added any products {isSingle ? 'to this campaign' : 'to these campaigns'} yet.
        </div>

        <Button variant="white" className="w-100 fw-bold" onClick={handleParticipate}>
          Participate
        </Button>
      </Card.Body>
    </Card>
  );
};

export default React.memo(CampaignAlert);
